import { createFeatureSelector, createSelector } from '@ngrx/store';
import * as fromAuth from '../auth/auth.reducer';
import { IUser, IAuthenticatedUser, Providers, Provider } from './user.entity';
import { selectRouterState } from '@core/state/ui/ui.reducer';
import { selectConfigState } from '@core/state/config/config.selectors';
import { selectEntityMap } from '@core/state/entities/entity.selectors';
import { selectGravatarServerImport } from '@app/generated/feature-flags.selectors';
import { pictureUrlForBrowser } from './profile-picture';

const GOOGLE_SIGN_IN = 'Google';
const APPLE_SIGN_IN = 'Apple';
const FACEBOOK_SIGN_IN = 'Facebook';
const AUTH0_SIGN_IN = 'Auth0';
export type SocialSignIn =
  | typeof GOOGLE_SIGN_IN
  | typeof APPLE_SIGN_IN
  | typeof FACEBOOK_SIGN_IN;

enum SocialProviders {
  google = 'Google',
  apple = 'Apple',
}

export interface ISocialSignInInfo {
  icon: string;
  name: SocialSignIn | 'Auth0';
  providerName: Provider;
}

export const AUTH0_SIGN_IN_INFO: ISocialSignInInfo = {
  name: AUTH0_SIGN_IN,
  icon: '',
  providerName: Providers.Auth0,
};
export const GOOGLE_SIGN_IN_INFO: ISocialSignInInfo = {
  name: GOOGLE_SIGN_IN,
  icon: 'google',
  providerName: Providers.Google,
};

export const APPLE_SIGN_IN_INFO: ISocialSignInInfo = {
  name: APPLE_SIGN_IN,
  icon: 'apple',
  providerName: Providers.Apple,
};
export const FACEBOOK_SIGN_IN_INFO: ISocialSignInInfo = {
  name: FACEBOOK_SIGN_IN,
  icon: 'facebook',
  providerName: Providers.Facebook,
};

export const SOCIAL_SIGN_INS: ISocialSignInInfo[] = [
  GOOGLE_SIGN_IN_INFO,
  // AppleSignInInfo,
  // FacebookSignInInfo,
];

export interface ISocialSignInMap {
  [provider: string]: ISocialSignInInfo;
}

export const socialSignInMap: ISocialSignInMap = {
  [Providers.Google]: GOOGLE_SIGN_IN_INFO,
  [Providers.Apple]: APPLE_SIGN_IN_INFO,
  [Providers.Facebook]: FACEBOOK_SIGN_IN_INFO,
  [Providers.Auth0]: AUTH0_SIGN_IN_INFO,
};

export interface ISocialSignInState {
  signInInfo: ISocialSignInInfo;
  linked: boolean;
  user_id: string | null;
  accountManagementStatus: fromAuth.AccountManagementStatus;
}

export const selectAuthState = createFeatureSelector<fromAuth.State>('auth');
export const selectAccountDeletionError = createSelector(
  selectAuthState,
  (state) => state.accountDeletionError
);
export const selectAccountDeletionInFlight = createSelector(
  selectAuthState,
  (state) => state.accountDeletionInFlight
);
export const selectIsSigningUp = createSelector(
  selectAuthState,
  (st) => st.signupPending
);
export const selectSignUpStatus = createSelector(selectAuthState, (st) =>
  st.signupPending ? 'pending' : st.signupResult
);
export const selectAuthenticatedUser = createSelector(
  selectAuthState,
  (st): IAuthenticatedUser | null => st.user ?? null
);
export const selectAuthenticatedUserSub = createSelector(
  selectAuthenticatedUser,
  (u) => u?.sub ?? null
);

export const selectAccountManagementStatus = createSelector(
  selectAuthState,
  (st): fromAuth.AccountManagementStatus => st.accountManagementStatus
);

/** The last account-management projection for the signed-in subject, as received. */
export const selectAccountManagement = createSelector(
  selectAuthState,
  (st) => st.accountManagement
);

/**
 * The Security page's summary (account portal slice 2): password, two-step
 * verification and passkeys, plus Google's link state and server-masked email.
 * `available` is false when the projection carries no security summary (Auth0,
 * the local no-op service, or an Identity build that predates it).
 */
export interface ISecuritySummary {
  available: boolean;
  hasPassword: boolean;
  twoFactorEnabled: boolean;
  passkeys: { name: string; createdAt: string }[];
  google: { linked: boolean; maskedEmail: string | null };
}

export const selectSecuritySummary = createSelector(
  selectAccountManagement,
  (account): ISecuritySummary => {
    const google = account?.linkedIdentities.find(
      (identity) => identity.provider === Providers.Google
    );
    const security = account?.security ?? null;
    return {
      available: security !== null,
      hasPassword: security?.hasPassword ?? false,
      twoFactorEnabled: security?.twoFactorEnabled ?? false,
      passkeys: (security?.passkeys ?? []).map((passkey) => ({
        name: passkey.name,
        createdAt: passkey.createdAt,
      })),
      google: {
        linked: google !== undefined,
        maskedEmail: google?.maskedEmail ?? null,
      },
    };
  }
);

export const selectAuthenticatedUserSocialSignIns = createSelector(
  selectAuthState,
  (authState) => {
    const au = authState.user;
    const identities = au?.identities;
    return identities
      ? SOCIAL_SIGN_INS.map((ssi) => {
          const linkedIdentity = identities.find(
            (identity) => ssi.providerName === identity.provider
          );
          return {
            signInInfo: ssi,
            linked: linkedIdentity !== undefined,
            user_id: linkedIdentity?.user_id ?? null,
            accountManagementStatus: authState.accountManagementStatus,
          };
        })
      : null;
  }
);

/** One linked sign-in method from the account-management projection. */
export interface ILinkedSignIn {
  /** Projected provider key, e.g. `google-oauth2`. */
  provider: string;
  /** Human-readable provider name. */
  name: string;
  /** Asset icon name, or null when SyRF has no icon for the provider. */
  icon: string | null;
  /** ISO timestamp the link was made, when the identity service records one. */
  linkedAt: string | null;
}

const displayProviderName = (provider: string): string =>
  provider ? provider.charAt(0).toUpperCase() + provider.slice(1) : 'Unknown';

/**
 * Every linked sign-in method from the account-management projection, in
 * projection order (unlike `selectAuthenticatedUserSocialSignIns`, which
 * reports only the providers SyRF offers to connect). Empty until a user is
 * loaded; read alongside `selectAccountManagementStatus`.
 */
export const selectLinkedSignIns = createSelector(
  selectAuthState,
  (authState): ILinkedSignIn[] =>
    (authState.user?.identities ?? []).map((identity) => {
      const known = socialSignInMap[identity.provider];
      return {
        provider: identity.provider,
        name: known?.name ?? displayProviderName(identity.provider),
        icon: known?.icon || null,
        linkedAt: identity.connected_at || null,
      };
    })
);

export const selectImpersonatedUser = createSelector(
  selectAuthState,
  (authState): IUser | null =>
    authState.impersonating ? authState.impersonating : null
);

export const selectCurrentUser = createSelector(
  selectAuthState,
  selectImpersonatedUser,
  (authState, impersonatedUser): IUser | null =>
    impersonatedUser ?? authState.user ?? null
);

export const selectCurrentUserId = createSelector(
  selectCurrentUser,
  (u: IAuthenticatedUser | IAuthenticatedUser | null): string | null =>
    u?.id?.toLowerCase() ?? null
);

export const selectAuthenticatedUserId = createSelector(
  selectAuthenticatedUser,
  (au): string | null => au?.id.toLowerCase() ?? null
);

export const selectIsAuthenticated = createSelector(
  selectAuthState,
  (st) => !!st.user
);
export const selectIsFullyAuthenticated = createSelector(
  selectAuthState,
  (st) => !!st.user?.fullAuth0
);

export const selectIsAuthenticating = createSelector(
  selectAuthState,
  (s) => s.isAuthenticating
);
export const selectAuthIsLoading = createSelector(
  selectAuthState,
  (s) => s.isLoading
);

export const selectAuthIsIdle = createSelector(
  selectIsAuthenticating,
  selectAuthIsLoading,
  (isAuthenticating, isLoading) => !isAuthenticating && !isLoading
);

export const selectIsAuthError = createSelector(
  selectAuthState,
  (s) => !!s.error
);

export const selectAuthErrorMessage = createSelector(
  selectAuthState,
  (s) => s.error && s.error.message
);

export const selectAuthErrorObject = createSelector(
  selectAuthState,
  (s) => s.error && s.error.object
);

/**
 * The Identity `syrf_groups` claim check. Since M2a this is only the **fallback** where PM
 * application authority does not decide (off/shadow): present application features with
 * `selectApplicationCapability`/`selectCanAdministerApplication` or `requireApplicationCapability`.
 * Retired with the claim itself in M7a.
 */
export const selectIsSyrfAdminUserAuthenticated = createSelector(
  selectAuthState,
  selectIsFullyAuthenticated,
  selectConfigState,
  (s, isFullyAuthenticated, appConfigState) =>
    isFullyAuthenticated &&
    (s.user?.syrf_groups?.some(
      (sg) => sg === 'administrator' || sg === 'SyrfAdmin'
    ) ??
      false)
);
export const selectIsImpersonating = createSelector(
  selectAuthState,
  (s) => !!s.impersonating
);

export const selectManagementTokenFromAuthState = createSelector(
  selectAuthState,
  (s) => s.state?.managementToken ?? null
);
export const selectIdToken = createSelector(selectAuthState, (s) => s.idToken);
export const selectManagementToken = createSelector(
  selectAuthState,
  (s) => s.managementAuthToken
);
export const selectUnderManagement = createSelector(selectRouterState, (s) =>
  s.url.startsWith('/manage')
);
/**
 * The current user's own avatar. With `gravatarServerImport` off it is the identity provider's `picture` claim, as
 * before. With it on, it is the stored picture from the user's Investigator record once that has loaded: under Auth0
 * (and for an account migrated to Identity) the claim stays the Gravatar URL even after the server has imported a
 * copy. A Gravatar URL is never returned while the flag is on (#4602).
 */
export const selectProfilePictureUrl = createSelector(
  selectCurrentUser,
  selectImpersonatedUser,
  selectEntityMap('investigators'),
  selectGravatarServerImport,
  (user, impersonatedUser, investigators, gravatarServerImport) => {
    if (!gravatarServerImport) {
      return user?.pictureUrl ?? null;
    }
    const stored =
      !impersonatedUser && user?.id ? investigators?.[user.id] : undefined;
    return pictureUrlForBrowser(
      stored ? stored.pictureUrl : user?.pictureUrl,
      true
    );
  }
);

export const selectAuthenticatedUserEmail = createSelector(
  selectAuthenticatedUser,
  (s) => s?.email ?? null
);
